import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Box, Layers, FileCode2, Download, Eye } from "lucide-react";
import { Page, Reveal, SectionTag } from "@/components/Reveal";
import { IMAGES, MATTERPORT_TOUR_URL } from "@/lib/data";

const SERVICES = [
  {
    id: "as-built",
    icon: FileText,
    title: "As-built surveys",
    image: IMAGES.blueprint,
    body: "We field-measure your retail space and turn it into clean, accurate drawings your design team can build from. Every dimension is taken on site, by us, so you're not relying on outdated landlord plans or best guesses.",
    note: "Delivered in DWG and PDF formats, ready for your architect, contractor, or construction manager.",
    ideal: ["New store build-outs", "Remodels", "Relocations", "Lease due diligence"],
  },
  {
    id: "matterport",
    icon: Box,
    title: "Matterport 3D scans",
    image: IMAGES.luxury,
    body: "A Matterport scan creates a complete 3D digital model of your space. Your team can walk through it from any computer or phone, check conditions, and take measurements without another trip to the site.",
    note: "Shared by link — ready for your whole team, wherever they are. We also perform Matterport surveys for real estate agents, and for individuals who would like a walk-through of their property.",
    ideal: ["Remote design teams", "Multi-site rollouts", "A permanent visual record before work begins", "Real estate agents & listings", "Individual property walk-throughs"],
  },
  {
    id: "combined",
    icon: Layers,
    title: "Survey plus scan",
    image: IMAGES.mall,
    body: "Most clients get the best value by combining both. The drawings give your architect precise dimensions, and the 3D scan answers the questions drawings can't — like ceiling conditions, existing fixtures, and storefront details.",
    note: "One visit, one crew, a complete picture of the space.",
    ideal: ["New store design packages", "Complex remodels", "Stakeholders in multiple cities"],
  },
];

const DELIVERABLES = [
  ["As-built floor plan", "DWG + PDF", "Dimensioned existing-conditions drawing for design and permitting"],
  ["Reflective ceiling & details", "DWG + PDF", "Ceiling heights, fixtures, and storefront conditions"],
  ["Matterport 3D tour", "Shareable link", "Walk the space remotely and measure inside the model"],
];

export default function Services() {
  return (
    <Page>
      <section className="blueprint-grid relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-36 sm:px-6 lg:px-8 lg:pt-44">
          <Reveal>
            <SectionTag>Services</SectionTag>
            <h1
              data-testid="services-headline"
              className="max-w-4xl font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
            >
              As-built surveys and Matterport scans for retail spaces
            </h1>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Before your architect draws a single line, they need to know what's really
              there. Global Dimensions measures your space in person and delivers accurate
              existing-conditions drawings and 3D scans, so your design starts from facts,
              not assumptions.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl space-y-24 px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id}>
            <div
              data-testid={`service-block-${s.id}`}
              className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-16 ${
                i % 2 === 1 ? "" : ""
              }`}
            >
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative">
                  <span className="absolute -left-3 -top-3 h-6 w-6 border-l-2 border-t-2 border-cad/70" />
                  <span className="absolute -bottom-3 -right-3 h-6 w-6 border-b-2 border-r-2 border-cad/70" />
                  <div className="overflow-hidden rounded-sm border border-white/10">
                    {s.id === "matterport" ? (
                      <iframe
                        src={MATTERPORT_TOUR_URL}
                        title="Sample Matterport 3D walkthrough of a surveyed space"
                        className="h-72 w-full bg-surface lg:h-96"
                        allow="xr-spatial-tracking; fullscreen"
                        allowFullScreen
                        loading="lazy"
                        data-testid="services-matterport-embed"
                      />
                    ) : (
                      <>
                        <img src={s.image} alt={s.title} className="h-72 w-full object-cover lg:h-96" />
                        <div className="absolute inset-0 bg-gradient-to-t from-canvas/70 to-transparent" />
                      </>
                    )}
                  </div>
                  <span
                    className={`pointer-events-none absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-cad ${
                      s.id === "matterport" ? "rounded-sm bg-canvas/80 px-2.5 py-1.5 backdrop-blur-sm" : ""
                    }`}
                  >
                    {s.id === "matterport"
                      ? "Live tour — drag to look around"
                      : `FIG. ${String(i + 1).padStart(2, "0")} — ${s.id.replace("-", " ")}`}
                  </span>
                </div>
              </div>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="flex items-center gap-3">
                  <s.icon size={22} className="text-laser" />
                  <h2 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                    {s.title}
                  </h2>
                </div>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400">{s.body}</p>
                <p className="mt-4 max-w-xl border-l-2 border-cad/50 pl-4 text-sm leading-relaxed text-slate-300">
                  {s.note}
                </p>
                <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">
                  Ideal for
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {s.ideal.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-sm border border-white/10 bg-surface px-3 py-1.5 text-xs text-slate-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="border-y border-white/5 bg-surface/40">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <Reveal>
            <SectionTag>Deliverables</SectionTag>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
              What lands in your inbox
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div data-testid="deliverables-table" className="mt-10 overflow-hidden rounded-md border border-white/10">
              <div className="hidden grid-cols-3 gap-px bg-white/10 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 sm:grid">
                <div className="bg-canvas p-4">Deliverable</div>
                <div className="bg-canvas p-4">Format</div>
                <div className="bg-canvas p-4">What it's for</div>
              </div>
              {DELIVERABLES.map(([d, f, use]) => (
                <div
                  key={d}
                  className="grid grid-cols-1 gap-1.5 border-t border-white/10 bg-canvas p-4 first:border-t-0 sm:grid-cols-3 sm:gap-px sm:bg-white/5 sm:p-0 sm:first:border-t"
                >
                  <div className="text-sm font-medium text-slate-200 sm:bg-canvas sm:p-4">{d}</div>
                  <div className="font-mono text-xs text-cad sm:bg-canvas sm:p-4">
                    <span className="mr-2 text-[9px] uppercase tracking-[0.2em] text-slate-400 sm:hidden">Format</span>
                    {f}
                  </div>
                  <div className="text-sm text-slate-400 sm:bg-canvas sm:p-4">{use}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <div
              data-testid="sample-dwg-card"
              className="blueprint-grid mt-6 flex flex-col items-start justify-between gap-6 rounded-md border border-cad/30 bg-canvas p-7 sm:flex-row sm:items-center"
            >
              <div className="flex items-start gap-4">
                <FileCode2 size={26} className="mt-1 shrink-0 text-cad" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cad/80">
                    Sample deliverable
                  </p>
                  <h3 className="mt-2 font-display text-lg font-semibold text-slate-100">
                    Download a real as-built survey
                  </h3>
                  <p className="mt-2 max-w-lg text-sm leading-relaxed text-slate-400">
                    A full existing-conditions drawing set from an actual project — open it in
                    AutoCAD or any DWG viewer to see the layering, dimensions, and detail your
                    team will receive.
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-3">
                <a
                  href="/samples/sample-as-built-survey.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sample-pdf-view"
                  className="group flex items-center gap-2 rounded-sm border border-cad/40 bg-cad/10 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-cad transition-colors duration-200 hover:bg-cad/20"
                >
                  <Eye size={15} />
                  View PDF
                </a>
                <a
                  href="/samples/sample-as-built-survey.dwg"
                  download
                  data-testid="sample-dwg-download"
                  className="group flex items-center gap-2 rounded-sm border border-cad/40 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-cad transition-colors duration-200 hover:border-cad hover:bg-cad/10"
                >
                  <Download size={15} />
                  DWG · 615 KB
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/quote"
              data-testid="services-quote-button"
              className="group mt-12 inline-flex items-center gap-2 rounded-sm bg-laser px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-canvas shadow-[0_0_32px_rgba(245,158,11,0.3)] transition-colors duration-200 hover:bg-amber-400"
            >
              Request a Quote
              <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
